import { Alert, Text } from "@mantine/core";
import type { Task } from "@medplum/fhirtypes";
import { IconAlertTriangle } from "@tabler/icons-react";
import { EncounterFormShell } from "@/components/domain/encounter/EncounterFormShell";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { forwardRef } from "react";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import type { EncounterFormRef } from "@/components/domain/encounter/EncounterFormShell";

/**
 * Activity codes that should render encounter-based completion content.
 * @agentic connections
 */
const ENCOUNTER_ACTIVITY_CODES = new Set([
  "check-in-on-member",
  "complete-cadenced-followup",
  "follow-up-on-cocm",
  "discharge-member",
]);

/**
 * Extracts encounter completion context values from a task.
 * @agentic connections
 */
const deriveEncounterContext = (task: Task) => {
  const activityCode = task.code?.coding?.[0]?.code;
  const carePlanRef = task.basedOn?.find((entry) =>
    entry.reference?.startsWith("CarePlan/"),
  )?.reference;
  const patientRef = task.for?.reference;

  const patientId = patientRef
    ? getResourceIdFromReference(patientRef, "Patient")
    : undefined;
  const carePlanId = carePlanRef
    ? getResourceIdFromReference(carePlanRef, "CarePlan")
    : undefined;

  if (
    !activityCode ||
    !ENCOUNTER_ACTIVITY_CODES.has(activityCode) ||
    !patientId ||
    !carePlanId ||
    !task.id
  ) {
    return undefined;
  }

  return {
    patientId,
    activityContext: {
      carePlanId,
      activityCode,
      taskId: task.id,
    },
  };
};

/**
 * Encounter-driven completion content for activity tasks.
 * @agentic connections
 */
export const EncounterCompletionContent = forwardRef<
  EncounterFormRef,
  CompletionContentProps
>(function EncounterCompletionContent(
  {
    task,
    onDone,
    onCancel,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    isResolvingNextTaskOwner = false,
  }: CompletionContentProps,
  ref,
) {
  const context = deriveEncounterContext(task);

  if (!context) {
    return (
      <Alert
        color="red"
        variant="light"
        icon={<IconAlertTriangle size={18} />}
        title="Unable to open encounter completion"
      >
        <Text size="sm">
          Missing patient or care plan context for this activity. Please refresh
          and try again.
        </Text>
      </Alert>
    );
  }

  return (
    <EncounterFormShell
      ref={ref}
      patientId={context.patientId}
      activityContext={context.activityContext}
      nextActivityLabel={nextActivityLabel}
      nextActivityDefaultOwner={nextActivityDefaultOwner}
      nextTaskOwnerOverride={nextTaskOwnerOverride}
      onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
      isResolvingNextTaskOwner={isResolvingNextTaskOwner}
      onSuccess={() => onDone()}
      onCancel={onCancel}
    />
  );
});
